/**
 * The runtime guard on OTA and build dispatch (U5, R25, KTD7).
 *
 * An OTA bundle or a build carries the `runtimeVersion` of `apps/mobile/version.json`
 * at the ref it was dispatched from. The guard accepts that ref for an app, per
 * platform, when the runtime matches a NON-RETIRED line the app has on that
 * platform. For a build, any record on the line counts, planned included. For an
 * OTA the app needs a binary on the line (preparing, in review, rejected, live or
 * superseded): KTD7 also counted planned lines, but once a new line is planned
 * fleet-wide that would let an OTA from `main` through to an app live only on the
 * old line, which R25 / AE5 refuse, and the Requirements win. Superseded still
 * counts because those devices keep running the old binary for weeks and get
 * their fixes through the backport OTA loop (`docs/platform-releases/versioning.md`).
 *
 * Pure and shared, so the backend's dispatch use-cases and the web dialogs'
 * override checkbox read one rule. The backend decides; the dialog only predicts.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-09-23-2135-feat-app-release-lines-and-school-drafts-plan.md (U5)
 */

import { z } from 'zod'
import {
  MOBILE_VERSION_TAG_PREFIX,
  MobileVersionTagLaneSchema,
  mobileVersionTagName,
  refEstablishesVersion
} from './mobile-version-tag'
import { PlatformSchema, type Platform } from './platform'
import {
  AppLineStateSchema,
  compareLineRuntimes,
  type AppLineState,
  ReleaseLineLifecycleSchema,
  type AppPlatformLines,
  type AppLineStateResult,
  type ReleaseLine
} from './release-line'

/**
 * `warn`: the guard computes what it would skip or refuse and returns it as a
 * warning, and the dispatch goes out as it did before the guard. The shipped mode.
 * `enforce`: skipped apps are not dispatched and a refused dispatch writes nothing.
 * Turned on after U2's one-time runtime backfill, so apps are not refused as unknown.
 */
export const RUNTIME_GUARD_MODES = ['warn', 'enforce'] as const
export const RuntimeGuardModeSchema = z.enum(RUNTIME_GUARD_MODES)
export type RuntimeGuardMode = z.infer<typeof RuntimeGuardModeSchema>

/**
 * One platform's answer:
 *
 *  - `accepted`: the runtime matches a non-retired line the app has.
 *  - `retired_line`: the runtime matches a line the app has, and the line is retired.
 *  - `no_matching_line`: the app has lines on this platform, none on this runtime.
 *  - `no_binary_on_line`: an OTA only. The app has the line (planned, blocked, or
 *    its store state unknown) but no binary on it yet, so no device of this app
 *    runs that runtime and an OTA would reach nobody.
 *  - `unknown_line`: the app has no line at all on this platform, so nothing can say
 *    what it runs. Skipped from a multi-school OTA, refused for a single app.
 */
export const RUNTIME_GUARD_VERDICTS = [
  'accepted',
  'retired_line',
  'no_matching_line',
  'no_binary_on_line',
  'unknown_line'
] as const
export const RuntimeGuardVerdictSchema = z.enum(RUNTIME_GUARD_VERDICTS)
export type RuntimeGuardVerdict = z.infer<typeof RuntimeGuardVerdictSchema>

/**
 * What is being dispatched, because the two need different evidence (R25, AE5):
 *
 *  - `build`: any non-retired line the app has a record on counts, planned and
 *    blocked included. A build from `main` for an app planned onto 1.9 is how
 *    its 1.9 rollout starts.
 *  - `ota`: only a line the app has a BINARY on counts, derived as preparing, in
 *    review, rejected, live or superseded. An OTA reaches only devices already
 *    running that runtime, so a line the app is merely planned on does not.
 */
export type RuntimeGuardDispatchKind = 'build' | 'ota'

/** The derived states that mean a binary of this app exists on the line. */
const BINARY_STATES: ReadonlySet<AppLineState> = new Set(['preparing', 'in_review', 'rejected', 'live', 'superseded'])

/** A line the app has on one platform, as the guard reports it beside a refusal. */
export const RuntimeGuardAppLineSchema = z.object({
  lineId: z.string(),
  name: z.string(),
  runtimeVersion: z.string(),
  lifecycle: ReleaseLineLifecycleSchema,
  state: AppLineStateSchema,
  /**
   * The app has had a binary on this line on this platform: its state says so,
   * or a store version was ever observed on it, or the record holds shipped
   * values. Monotonic on purpose: a binary on devices stays there, so a stale
   * store read (state `unknown`) must not make an OTA to that runtime refused.
   */
  hasBinary: z.boolean()
})
export type RuntimeGuardAppLine = z.infer<typeof RuntimeGuardAppLineSchema>

export const RuntimeGuardPlatformCheckSchema = z.object({
  platform: PlatformSchema,
  verdict: RuntimeGuardVerdictSchema,
  /** Every line the app has on this platform, newest runtime first. */
  lines: z.array(RuntimeGuardAppLineSchema)
})
export type RuntimeGuardPlatformCheck = z.infer<typeof RuntimeGuardPlatformCheckSchema>

/** One app's answer: accepted only when every platform in scope accepts. */
export const RuntimeGuardAppCheckSchema = z.object({
  /** `MobileApp.publicId`. */
  appId: z.string(),
  appName: z.string(),
  /** The registry school key, on an OTA. */
  schoolKey: z.string().optional(),
  accepted: z.boolean(),
  platforms: z.array(RuntimeGuardPlatformCheckSchema),
  /** Why it was not accepted, in words; empty when accepted. */
  reason: z.string()
})
export type RuntimeGuardAppCheck = z.infer<typeof RuntimeGuardAppCheckSchema>

// -----------------------------------------------------------------------------
// Release source and version identity (SCH-2563 U4; R5, R6, R7; KTD5, KTD6)
// -----------------------------------------------------------------------------

/**
 * Where a production release ships from (R6):
 *
 *  - `establishing`: `main` or a `release/mobile-*` branch, the refs whose first
 *    production success establishes a version (`refEstablishesVersion`).
 *  - `version_tag`: a `mobile/v*` tag, which ships a version already established.
 *  - `other`: anything else (`dev`, a feature branch, an old `v1.9` branch). It
 *    ships, but it never establishes a version and it is flagged.
 */
export const RELEASE_SOURCE_CLASSES = ['establishing', 'version_tag', 'other'] as const
export const ReleaseSourceClassSchema = z.enum(RELEASE_SOURCE_CLASSES)
export type ReleaseSourceClass = z.infer<typeof ReleaseSourceClassSchema>

/** Classify a dispatched ref by name alone, so the dialogs predict exactly what the backend records. */
export function classifyReleaseSource(ref: string): ReleaseSourceClass {
  if (refEstablishesVersion(ref)) return 'establishing'
  if (ref.startsWith(MOBILE_VERSION_TAG_PREFIX)) return 'version_tag'
  return 'other'
}

/**
 * The release-source warning for a production dispatch, or null (R6, KTD6).
 * Independent of the guard mode: it is a warning in every mode and never a
 * refusal. Preview and development dispatches get none (R7), which is the
 * caller's to decide; this reads the ref only.
 */
export function releaseSourceWarning(ref: string): string | null {
  if (classifyReleaseSource(ref) !== 'other') return null
  return (
    `Production release from "${ref}", which is not main, a release/mobile-* branch or a mobile/v* tag. ` +
    'It ships, but it never establishes a version, and its code may differ from the version it is labelled with.'
  )
}

/** How a version's commit was established: its `mobile/v*` tag, or (untagged) its earliest eligible success. */
export const VERSION_ESTABLISHED_BY = ['tag', 'release'] as const
export const VersionEstablishedBySchema = z.enum(VERSION_ESTABLISHED_BY)
export type VersionEstablishedBy = z.infer<typeof VersionEstablishedBySchema>

/**
 * A successful production release of the version from a ref that cannot
 * establish one (`dev`, a feature branch). Listed beside the identity finding
 * for context, never as a warning (R5).
 */
export const VersionIdentityContextReleaseSchema = z.object({
  /** `AppRelease.publicId`. */
  releaseId: z.string(),
  ref: z.string(),
  commit: z.string(),
  lane: MobileVersionTagLaneSchema,
  /** When its first success was recorded, ISO 8601. */
  succeededAt: z.string()
})
export type VersionIdentityContextRelease = z.infer<typeof VersionIdentityContextReleaseSchema>

/**
 * What a marketing version names (KTD5): its tag's commit, else the commit of
 * its earliest successful production release from `main` or `release/mobile-*`
 * (the same read the tag writer uses, so the two never disagree), else nothing
 * yet. `context` lists its successful releases from other refs.
 */
export const VersionIdentitySchema = z.object({
  version: z.string(),
  /** Null: no tag and no eligible success yet, so the version is not established. */
  establishedCommit: z.string().nullable(),
  establishedBy: VersionEstablishedBySchema.nullable(),
  /** The tag name when established by tag; the establishing release's ref when by release. */
  establishedRef: z.string().nullable(),
  context: z.array(VersionIdentityContextReleaseSchema)
})
export type VersionIdentity = z.infer<typeof VersionIdentitySchema>

/** The identity read set against the commit being dispatched. */
export const VersionIdentityFindingSchema = VersionIdentitySchema.extend({
  /** The commit the dispatched ref resolves to. */
  commit: z.string(),
  /** The version is established at a different commit: the warning (or, in `enforce`, the refusal). */
  mismatch: z.boolean()
})
export type VersionIdentityFinding = z.infer<typeof VersionIdentityFindingSchema>

/**
 * The finding for dispatching `commit` under `identity`'s version, or null when
 * there is nothing to say: the version is new (or established at this very
 * commit) and no other-ref release needs listing.
 */
export function versionIdentityFinding(identity: VersionIdentity, commit: string): VersionIdentityFinding | null {
  const mismatch = identity.establishedCommit !== null && identity.establishedCommit !== commit
  if (!mismatch && identity.context.length === 0) return null
  return { ...identity, commit, mismatch }
}

const short = (sha: string) => sha.slice(0, 7)

/** The words for a mismatched finding: the version, both commits, and what established the version. */
export function versionIdentityMessage(finding: VersionIdentityFinding, ref: string): string {
  const by =
    finding.establishedBy === 'tag'
      ? `its tag ${mobileVersionTagName(finding.version)}`
      : `its first production release from ${finding.establishedRef ?? 'an eligible ref'}`
  return (
    `Version ${finding.version} is established at ${short(finding.establishedCommit ?? '')} (${by}), ` +
    `but "${ref}" is at ${short(finding.commit)}. Ship ${mobileVersionTagName(finding.version)}, or bump the version for new code.`
  )
}

/**
 * What the guard did on one dispatch, returned with the dispatch result.
 *
 * In `enforce`, `skipped` lists the apps left out of a multi-school OTA. In
 * `warn`, it lists the apps the guard WOULD have skipped or refused, and
 * `warning` says so; everything was dispatched.
 *
 * The SCH-2563 fields are optional so a report recorded before them still
 * parses. `identity` and `sourceWarning` are set on production dispatches only
 * (R7). A version-identity mismatch is a dispatch-level fact like an unreadable
 * ref: in `warn` its words join `warning`; in `enforce` it refuses the dispatch
 * unless overridden, and `overridden` records the override.
 */
export const RuntimeGuardReportSchema = z.object({
  mode: RuntimeGuardModeSchema,
  ref: z.string(),
  /** Marketing version on the ref; null when the ref could not be read (only possible in `warn`). */
  version: z.string().nullable(),
  runtimeVersion: z.string().nullable(),
  skipped: z.array(RuntimeGuardAppCheckSchema),
  /** A single-app dispatch the guard refuses went out because the request said override. */
  overridden: z.boolean(),
  warning: z.string().optional(),
  /** The commit the ref resolved to at dispatch; null when the read failed (only possible in `warn`). */
  commit: z.string().nullable().optional(),
  /** The version-identity finding (R5); absent when there is nothing to say. */
  identity: VersionIdentityFindingSchema.optional(),
  /** The release-source warning (R6), in every mode; never a refusal. */
  sourceWarning: z.string().optional()
})
export type RuntimeGuardReport = z.infer<typeof RuntimeGuardReportSchema>

const PLATFORM_WORD: Record<Platform, string> = { IOS: 'iOS', ANDROID: 'Android' }

const platformLines = (app: { ios: AppPlatformLines | null; android: AppPlatformLines | null }, platform: Platform) =>
  platform === 'IOS' ? app.ios : app.android

/**
 * The lines an app HAS on a platform: a record or a store version on it. A line
 * derived as `unknown` / `no_record` is one the app is simply not on (the
 * summary's `untracked`), so it is left out.
 */
export function runtimeGuardAppLines(derived: AppPlatformLines, lines: readonly ReleaseLine[]): RuntimeGuardAppLine[] {
  const fleet = new Map(lines.map(line => [line.id, line]))
  return derived.lines
    .filter((result: AppLineStateResult) => !(result.state === 'unknown' && result.reason === 'no_record'))
    .flatMap(result => {
      const line = fleet.get(result.lineId)
      return line
        ? [
            {
              lineId: line.id,
              name: line.name,
              runtimeVersion: line.runtimeVersion,
              lifecycle: line.lifecycle,
              state: result.state,
              hasBinary: BINARY_STATES.has(result.state) || !!(result.shipped || result.live || result.inFlight)
            }
          ]
        : []
    })
    .sort((a, b) => compareLineRuntimes(b.runtimeVersion, a.runtimeVersion))
}

function verdictFor(
  lines: readonly RuntimeGuardAppLine[],
  runtimeVersion: string,
  kind: RuntimeGuardDispatchKind
): RuntimeGuardVerdict {
  if (lines.length === 0) return 'unknown_line'
  const match = lines.find(line => line.runtimeVersion === runtimeVersion)
  if (!match) return 'no_matching_line'
  if (match.lifecycle === 'retired') return 'retired_line'
  if (kind === 'ota' && !match.hasBinary) return 'no_binary_on_line'
  return 'accepted'
}

/** "1.8 live, 1.9 planned", or "no line". */
export function runtimeGuardLinesText(lines: readonly RuntimeGuardAppLine[]): string {
  if (lines.length === 0) return 'no line'
  return lines
    .map(line => `${line.name} ${line.lifecycle === 'retired' ? 'retired' : line.state.replaceAll('_', ' ')}`)
    .join(', ')
}

function platformReason(check: RuntimeGuardPlatformCheck, runtimeVersion: string): string {
  const word = PLATFORM_WORD[check.platform]
  const has = runtimeGuardLinesText(check.lines)
  switch (check.verdict) {
    case 'accepted':
      return ''
    case 'retired_line':
      return `${word}: the ${runtimeVersion} line is retired (app's lines: ${has})`
    case 'no_matching_line':
      return `${word}: no line on runtime ${runtimeVersion} (app's lines: ${has})`
    case 'no_binary_on_line': {
      const match = check.lines.find(line => line.runtimeVersion === runtimeVersion)
      const name = match?.name ?? runtimeVersion
      const state = match ? match.state.replaceAll('_', ' ') : 'on the line'
      return `${word}: ${state} on ${name} but no ${name} binary yet (app's lines: ${has})`
    }
    case 'unknown_line':
      return `${word}: the app's line is unknown, so runtime ${runtimeVersion} cannot be confirmed`
  }
}

export interface CheckAppRuntimeInput {
  runtimeVersion: string
  /** Every fleet line, for names and lifecycles. */
  lines: readonly ReleaseLine[]
  /** The app's derived lines (`deriveCurrentAndUpcoming` per platform). A null platform is out of store scope. */
  app: {
    id: string
    name: string | null
    displayName: string | null
    ios: AppPlatformLines | null
    android: AppPlatformLines | null
  }
  /** The platforms the dispatch covers. */
  platforms: readonly Platform[]
  /** An OTA needs a binary on the line; a build needs only a record on it. */
  kind: RuntimeGuardDispatchKind
  schoolKey?: string
}

/**
 * Does this app accept a ref whose runtime is `runtimeVersion`, on every
 * platform the dispatch covers?
 *
 * A covered platform that is out of the app's store scope (an iOS-only app on an
 * `both` OTA) is not judged: the app never ships there, and judging it would
 * refuse every iOS-only app on every two-platform dispatch. When NONE of the
 * covered platforms is in scope, nothing says what the app runs, so each is
 * `unknown_line`.
 */
export function checkAppRuntime(input: CheckAppRuntimeInput): RuntimeGuardAppCheck {
  const { runtimeVersion, app, platforms } = input

  const inScope = platforms.filter(platform => platformLines(app, platform) !== null)
  const checks: RuntimeGuardPlatformCheck[] =
    inScope.length === 0
      ? platforms.map(platform => ({ platform, verdict: 'unknown_line' as const, lines: [] }))
      : inScope.map(platform => {
          const lines = runtimeGuardAppLines(platformLines(app, platform) as AppPlatformLines, input.lines)
          return { platform, verdict: verdictFor(lines, runtimeVersion, input.kind), lines }
        })

  const accepted = checks.every(check => check.verdict === 'accepted')
  return {
    appId: app.id,
    appName: app.displayName || app.name || app.id,
    ...(input.schoolKey ? { schoolKey: input.schoolKey } : {}),
    accepted,
    platforms: checks,
    reason: accepted
      ? ''
      : checks
          .filter(check => check.verdict !== 'accepted')
          .map(check => platformReason(check, runtimeVersion))
          .join('; ')
  }
}

/**
 * The git ref a single-app OTA or rebuild defaults to: the app's current line's
 * `gitRef` (R25). iOS and Android can sit on different current lines for a whole
 * rollout (AE4); a dialog that covers both then defaults to the OLDER current
 * line's ref, because a ref on the newer runtime would be refused for the platform
 * still on the older line. Null when no covered platform has a current line with
 * a ref.
 */
export function defaultDispatchRef(
  app: { ios: AppPlatformLines | null; android: AppPlatformLines | null },
  lines: readonly ReleaseLine[],
  platforms: readonly Platform[] = ['IOS', 'ANDROID']
): string | null {
  const fleet = new Map(lines.map(line => [line.id, line]))
  const current = platforms
    .map(platform => platformLines(app, platform)?.current)
    .flatMap(position => (position?.kind === 'line' ? [fleet.get(position.lineId)] : []))
    .filter((line): line is ReleaseLine => !!line && !!line.gitRef)
    .sort((a, b) => compareLineRuntimes(a.runtimeVersion, b.runtimeVersion))
  return current[0]?.gitRef ?? null
}
